import { useMemo } from 'react';
import {
  Activity,
  AlertCircle,
  BarChart3,
  Droplets,
  Fuel,
  TrendingUp,
} from 'lucide-react';
import {
  computeAnalyticsMetrics,
  WEEKLY_FUEL_DISPATCH_JUNE_2026,
} from './mockAnalyticsData';

const P = {
  silver: '#F1F5F9',
  white: '#FFFFFF',
  grid: '#E2E8F0',
  blue: '#3B82F6',
  blueTint: '#EFF6FF',
  text: '#1E293B',
  muted: '#64748B',
  subtle: '#94A3B8',
  orange: '#F97316',
  orangeTint: '#FFF7ED',
};


const metricCard = {
  backgroundColor: P.white,
  borderColor: P.grid,
};

function formatLiters(value) {
  return `${Math.round(value).toLocaleString('en-AE')} L`;
}

function formatAed(value) {
  return `AED ${Math.round(value).toLocaleString('en-AE')}`;
}

function MetricCard({ icon: Icon, label, value, subtext, children }) {
  return (
    <div
      className="rounded-xl border p-5 shadow-sm transition-shadow hover:shadow-md"
      style={metricCard}
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <div
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full"
          style={{
            backgroundColor: P.blueTint,
            boxShadow: `inset 0 0 0 2px ${P.white}, 0 0 0 1px ${P.grid}`,
          }}
        >
          <Icon className="h-5 w-5" style={{ color: P.blue }} strokeWidth={1.75} />
        </div>
        {children}
      </div>
      <p className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: P.muted }}>
        {label}
      </p>
      <p className="mt-1 text-2xl font-bold tabular-nums tracking-tight" style={{ color: P.text }}>
        {value}
      </p>
      {subtext ? (
        <p className="mt-1 text-xs" style={{ color: P.subtle }}>
          {subtext}
        </p>
      ) : null}
    </div>
  );
}

function WeeklyDispatchChart({ data }) {
  const chartWidth = 640;
  const chartHeight = 260;
  const padLeft = 52;
  const padRight = 16;
  const padTop = 20;
  const padBottom = 48;
  const plotW = chartWidth - padLeft - padRight;
  const plotH = chartHeight - padTop - padBottom;

  const maxLiters = Math.max(...data.map((d) => d.liters), 1);
  const yTicks = 4;
  const barGap = 12;
  const barWidth = (plotW - barGap * (data.length - 1)) / data.length;

  const gridLines = useMemo(() => {
    return Array.from({ length: yTicks + 1 }, (_, i) => {
      const ratio = i / yTicks;
      const y = padTop + plotH * (1 - ratio);
      const value = Math.round(maxLiters * ratio);
      return { y, value };
    });
  }, [maxLiters, plotH, padTop, yTicks]);

  return (
    <div className="overflow-x-auto">
      <svg
        viewBox={`0 0 ${chartWidth} ${chartHeight}`}
        className="mx-auto w-full max-w-full"
        role="img"
        aria-label="Weekly fuel dispatch volume trends for June 2026"
      >
        <rect x="0" y="0" width={chartWidth} height={chartHeight} fill={P.white} rx="8" />

        {gridLines.map(({ y, value }) => (
          <g key={value}>
            <line
              x1={padLeft}
              y1={y}
              x2={chartWidth - padRight}
              y2={y}
              stroke={P.grid}
              strokeWidth="1"
            />
            <text
              x={padLeft - 8}
              y={y + 4}
              textAnchor="end"
              fill={P.subtle}
              fontSize="10"
              fontFamily="system-ui, sans-serif"
            >
              {(value / 1000).toFixed(0)}k
            </text>
          </g>
        ))}

        {data.map((row, index) => {
          const barH = (row.liters / maxLiters) * plotH;
          const x = padLeft + index * (barWidth + barGap);
          const y = padTop + plotH - barH;

          return (
            <g key={row.day}>
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={barH}
                rx="4"
                fill={P.blue}
                opacity="0.92"
              />
              <text
                x={x + barWidth / 2}
                y={chartHeight - padBottom + 16}
                textAnchor="middle"
                fill={P.text}
                fontSize="11"
                fontWeight="600"
                fontFamily="system-ui, sans-serif"
              >
                {row.day}
              </text>
              <text
                x={x + barWidth / 2}
                y={chartHeight - padBottom + 30}
                textAnchor="middle"
                fill={P.subtle}
                fontSize="9"
                fontFamily="system-ui, sans-serif"
              >
                {row.dateLabel}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function AlertIndicators({ pending, inTransit }) {
  const slots = [
    ...Array.from({ length: Math.min(pending, 4) }, (_, i) => ({
      key: `pending-${i}`,
      color: P.orange,
      bg: P.orangeTint,
      title: 'Pending arrival',
    })),
    ...Array.from({ length: Math.min(inTransit, 3) }, (_, i) => ({
      key: `transit-${i}`,
      color: P.blue,
      bg: P.blueTint,
      title: 'In transit',
    })),
  ];

  if (!slots.length) {
    return (
      <span className="text-xs font-medium" style={{ color: P.subtle }}>
        All clear
      </span>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {slots.map((slot) => (
        <span
          key={slot.key}
          title={slot.title}
          className="inline-flex h-8 w-8 items-center justify-center rounded-full shadow-sm"
          style={{
            backgroundColor: slot.bg,
            boxShadow: `inset 0 0 0 2px ${P.white}, 0 0 0 2px ${slot.color}`,
          }}
        >
          <AlertCircle className="h-4 w-4" style={{ color: slot.color }} strokeWidth={2} />
        </span>
      ))}
      <span className="text-xs font-semibold tabular-nums" style={{ color: P.muted }}>
        {pending + inTransit} active
      </span>
    </div>
  );
}

export default function AnalyticsDashboard() {
  const metrics = useMemo(() => computeAnalyticsMetrics(), []);
  const weeklyTotal = useMemo(
    () => WEEKLY_FUEL_DISPATCH_JUNE_2026.reduce((sum, row) => sum + row.liters, 0),
    [],
  );

  return (
    <>
      <header className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="mb-2 flex items-center gap-2">
            <BarChart3 className="h-6 w-6" style={{ color: P.blue }} strokeWidth={1.75} />
            <span
              className="rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider"
              style={{ backgroundColor: P.blueTint, color: P.blue }}
            >
              Home · June 2026
            </span>
          </div>
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl" style={{ color: P.text }}>
            Analytics &amp; Dashboard Overview
          </h1>
          <p className="mt-1 max-w-2xl text-sm" style={{ color: P.muted }}>
            Fleet freight volume, terminal utilization, margin pipeline, and weekly dispatch trends
            across OGTS LogiCore™ operations.
          </p>
        </div>
      </header>

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          icon={Droplets}
          label="Total Fleet Freight Vol"
          value={formatLiters(metrics.totalFleetFreightLiters)}
          subtext="Accumulated liters dispatched · live rollup"
        />

        <MetricCard
          icon={Fuel}
          label="Active Terminal Capacity"
          value={`${metrics.terminalCapacityPct}%`}
          subtext="Simulated tank utilization · UAE terminals"
        >
          <div className="flex-1 pt-1">
            <div
              className="h-2 w-full overflow-hidden rounded-full"
              style={{ backgroundColor: P.grid }}
            >
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{
                  width: `${metrics.terminalCapacityPct}%`,
                  backgroundColor: P.blue,
                }}
              />
            </div>
          </div>
        </MetricCard>

        <MetricCard
          icon={TrendingUp}
          label="Net Profit Pipeline"
          value={formatAed(metrics.netProfitPipelineAed)}
          subtext="Consolidated AED margin · blending + freight"
        />

        <MetricCard
          icon={Activity}
          label="Active Dispatched Alerts"
          value={`${metrics.alerts.total}`}
          subtext="Pending arrivals & in-transit dispatches"
        >
          <AlertIndicators
            pending={metrics.alerts.pendingArrival}
            inTransit={metrics.alerts.inTransit}
          />
        </MetricCard>
      </div>

      <section
        className="rounded-xl border p-5 shadow-sm sm:p-6"
        style={{ ...metricCard, backgroundColor: P.white }}
      >
        <div className="mb-5 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-bold" style={{ color: P.text }}>
              Weekly Fuel Dispatch Volume Trends
            </h2>
            <p className="text-sm" style={{ color: P.muted }}>
              Sunday – Saturday · 7–13 June 2026 · {formatLiters(weeklyTotal)} weekly total
            </p>
          </div>
          <span
            className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold"
            style={{ borderColor: P.grid, color: P.blue, backgroundColor: P.blueTint }}
          >
            <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: P.blue }} />
            Dispatch volume (L)
          </span>
        </div>

        <div
          className="rounded-lg border p-3 sm:p-4"
          style={{ borderColor: P.grid, backgroundColor: P.silver }}
        >
          <WeeklyDispatchChart data={WEEKLY_FUEL_DISPATCH_JUNE_2026} />
        </div>
      </section>
    </>
  );
}
